import { Trans } from "@lingui/react/macro";

import { useAppConfig } from "~/hook/domain/useAppConfig.ts";
import { usePoke } from "~/hook/domain/usePoke.ts";
import { createDraggableSheet } from "~/ui/base/DraggableSheet.tsx";
import { PokeWithAudioSheet } from "~/ui/overlay/PokeWithAudioSheet.tsx";
import { PokeWithDrawingSheet } from "~/ui/overlay/PokeWithDrawingSheet.tsx";
import { PokeWithEmojiSheet } from "~/ui/overlay/PokeWithEmojiSheet.tsx";
import { PokeWithGeoLocationSheet } from "~/ui/overlay/PokeWithGeolocationSheet.tsx";
import { PokeWithPictureSheet } from "~/ui/overlay/PokeWithPictureSheet.tsx";
import { Layer, useStackedLayer } from "~/ui/provider/StackedLayerProvider.tsx";

const cx = {
  pokeButton:
    "h-12 rounded-2xl bg-zinc-100 dark:bg-zinc-800 px-4 text-start font-semibold duration-200 active:opacity-60 disabled:opacity-60 text-zinc-900 dark:text-zinc-100",
};

export const PokeSheet = createDraggableSheet<{ targetUserEmail: string }>(
  ({ close, context, titleId }) => {
    const { isMutating, trigger } = usePoke();
    const {
      isAudioPokeEnabled,
      isDrawingPokeEnabled,
      isEmojiPokeEnabled,
      isGeolocationPokeEnabled,
      isNormalPokeEnabled,
      isPicturePokeEnabled,
    } = useAppConfig();
    const overlay = useStackedLayer();

    const closeAndOpen = (Sheet: Layer<{ email: string }>) => {
      close();
      setTimeout(() => {
        overlay(Sheet, { email: context.targetUserEmail });
      }, 200);
    };

    const normalPoke = () => {
      void trigger({
        email: context.targetUserEmail,
        payload: { type: "normal" },
      }).finally(() => {
        close();
      });
    };

    return (
      <div className="p-6 pt-0" data-testid="콕찌르기 시트">
        <h1
          className="pt-4 text-lg font-semibold text-zinc-800 dark:text-zinc-100"
          id={titleId}
        >
          <Trans>콕! 찌르기 👉</Trans>
        </h1>
        <div className="flex flex-col gap-4 pt-6">
          {isAudioPokeEnabled && (
            <button
              className={cx.pokeButton}
              disabled={isMutating}
              onClick={() => {
                closeAndOpen(PokeWithAudioSheet);
              }}
            >
              <Trans>소리 찌르기 🎙️</Trans>
            </button>
          )}
          {isPicturePokeEnabled && (
            <button
              className={cx.pokeButton}
              disabled={isMutating}
              onClick={() => {
                closeAndOpen(PokeWithPictureSheet);
              }}
            >
              <Trans>사진 찌르기 📸</Trans>
            </button>
          )}
          {isDrawingPokeEnabled && (
            <button
              className={cx.pokeButton}
              disabled={isMutating}
              onClick={() => {
                closeAndOpen(PokeWithDrawingSheet);
              }}
            >
              <Trans>그림 찌르기 🎨</Trans>
            </button>
          )}
          {isGeolocationPokeEnabled && (
            <button
              className={cx.pokeButton}
              disabled={isMutating}
              onClick={() => {
                closeAndOpen(PokeWithGeoLocationSheet);
              }}
            >
              <Trans>내 위치 찌르기 📍</Trans>
            </button>
          )}
          {isEmojiPokeEnabled && (
            <button
              className={cx.pokeButton}
              disabled={isMutating}
              onClick={() => {
                closeAndOpen(PokeWithEmojiSheet);
              }}
            >
              <Trans>이모티콘 찌르기 😊</Trans>
            </button>
          )}
          {isNormalPokeEnabled && (
            <button
              className={cx.pokeButton}
              disabled={isMutating}
              onClick={normalPoke}
            >
              <Trans>바로 콕! 찌르기 👉</Trans>
            </button>
          )}
        </div>
      </div>
    );
  },
);
